
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatBatteries = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Batteries - Rachat Batteries Auto, Moto, Industrielles | Groupe Fabre"
        description="Rachat de batteries au meilleur prix : automobiles, motos, industrielles, onduleurs. Collecte et recyclage écologique."
        canonical="/nos-services/achat-metaux/batteries"
        keywords="achat batteries, rachat batteries auto, batteries usagées, recyclage batteries, prix batteries"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Zap className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Batteries</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat et recyclage écologique de batteries</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de batteries - Recyclage responsable
                </h2>
                <p className="text-gray-600 mb-6">
                  Les <strong>batteries usagées</strong> contiennent des matériaux précieux et polluants. 
                  <strong>Groupe Fabre Environnement</strong> assure leur collecte et recyclage 
                  dans le respect de l'environnement, tout en vous proposant une juste valorisation.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de batteries rachetées</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-600 text-white mr-3">•</span>
                    <span><strong>Automobiles :</strong> voitures, camions, utilitaires</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-600 text-white mr-3">•</span>
                    <span><strong>Motos :</strong> scooters, motos, quads</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-600 text-white mr-3">•</span>
                    <span><strong>Industrielles :</strong> chariots élévateurs, équipements</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-600 text-white mr-3">•</span>
                    <span><strong>Onduleurs :</strong> serveurs, équipements informatiques</span>
                  </li>
                </ul>

                <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                  <h4 className="font-bold text-green-800 mb-3">🌱 Engagement écologique</h4>
                  <ul className="text-green-700 text-sm space-y-2">
                    <li>• Recyclage 95% des composants</li>
                    <li>• Récupération plomb et plastiques</li>
                    <li>• Traitement acide sécurisé</li>
                    <li>• Respect directive européenne</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Service batteries</h3>
                <p className="text-gray-600 mb-6">
                  Notre service spécialisé batteries vous propose une collecte sécurisée 
                  et une valorisation optimale de vos batteries usagées.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-green-600 mr-3" />
                    <span>Rachat selon poids</span>
                  </div>
                  <div className="flex items-center">
                    <Truck className="h-5 w-5 text-green-600 mr-3" />
                    <span>Collecte sécurisée</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-green-600 mr-3" />
                    <span>Recyclage certifié</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 transition-colors">
                    Demander collecte batteries
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Process de recyclage */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Processus de recyclage des batteries
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold">1</span>
                </div>
                <h3 className="font-bold text-gray-700 mb-3">Collecte</h3>
                <p className="text-sm text-gray-600">
                  Enlèvement sécurisé sur votre site
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <h3 className="font-bold text-gray-700 mb-3">Tri</h3>
                <p className="text-sm text-gray-600">
                  Classification par type et composition
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold">3</span>
                </div>
                <h3 className="font-bold text-gray-700 mb-3">Démontage</h3>
                <p className="text-sm text-gray-600">
                  Séparation plomb, plastique, acide
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-green-600 font-bold">4</span>
                </div>
                <h3 className="font-bold text-gray-700 mb-3">Recyclage</h3>
                <p className="text-sm text-gray-600">
                  Transformation en nouvelles matières premières
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatBatteries;
